import React from 'react';

const TRACK_HEIGHT = 2;
const TRACK_HEIGHT_HOVER = 3;
const THUMB_RADIUS = 6;
const THUMB_HOVER_HALO = 16;
const THUMB_ACTIVE_HALO = 20;
const CANVAS_HEIGHT = 40;
const TRACK_COLOR = 'rgb(128, 62, 65)';
const FILL_COLOR = 'rgb(255, 82, 82)';
const THUMB_COLOR = 'rgb(255, 82, 82)';
const DISABLED_COLOR = 'rgba(255, 255, 255, 0.12)';

/**
 * Implemented via Canvas to fix UI stuttering caused by React re-renders on every audio tick.
 * The previous declarative approach (MUI Slider) struggled to maintain smoothness because 
 * its position relied directly on state props, causing jumps or performance degradation.
 * * This imperative solution decouples the visual loop from the logic loop:
 * 1. Visuals are interpolated via requestAnimationFrame at 60fps for fluid motion.
 * 2. Heavy Redux state updates are throttled (e.g., ~100ms) to prevent bottlenecks.
 */

class ProgressSlider extends React.Component {
    _canvasRef = React.createRef();
    _containerRef = React.createRef();
    _rafId = null;
    _dragging = false;
    _hovering = false;
    _currentValue = 0;
    _targetValue = 0;
    _canvasWidth = 0;
    _canvasHeight = 0;
    _dpr = window.devicePixelRatio || 1;
    _currentHaloR = 0;

    componentDidMount() {
        this._currentValue = this.props.value || 0;
        this._targetValue = this._currentValue;
        this._setupCanvas();
        this._startRenderLoop();
        window.addEventListener('resize', this._handleResize);
    }

    componentDidUpdate(prevProps) {
        if (!this._dragging) {
            const newValue = this.props.value || 0;
            const prevValue = prevProps.value || 0;
            const isLargeJump = Math.abs(newValue - prevValue) > 1 && newValue < prevValue;
            this._targetValue = newValue;
            if (isLargeJump) {
                this._currentValue = newValue;
            }
        }
    }

    componentWillUnmount() {
        this._stopRenderLoop();
        window.removeEventListener('resize', this._handleResize);
        window.removeEventListener('mousemove', this._handleWindowMouseMove);
        window.removeEventListener('mouseup', this._handleWindowMouseUp);
    }

    _setupCanvas = () => {
        const canvas = this._canvasRef.current;
        const container = this._containerRef.current;
        if (!canvas || !container) return;

        const rect = container.getBoundingClientRect();
        this._canvasWidth = rect.width;
        this._canvasHeight = CANVAS_HEIGHT;
        this._dpr = window.devicePixelRatio || 1;

        canvas.width = this._canvasWidth * this._dpr;
        canvas.height = this._canvasHeight * this._dpr;
        canvas.style.width = this._canvasWidth + 'px';
        canvas.style.height = this._canvasHeight + 'px';
    }

    _handleResize = () => {
        this._setupCanvas();
    }

    _startRenderLoop = () => {
        const render = () => {
            this._draw();
            this._rafId = requestAnimationFrame(render);
        };
        this._rafId = requestAnimationFrame(render);
    }

    _stopRenderLoop = () => {
        if (this._rafId != null) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
        }
    }

    _draw = () => {
        const canvas = this._canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const dpr = this._dpr;
        const w = this._canvasWidth;
        const h = this._canvasHeight;
        const { max, disabled } = this.props;
        const duration = max || 1;

        // Smooth interpolation toward target value
        const lerpFactor = this._dragging ? 0.5 : 0.18;
        this._currentValue += (this._targetValue - this._currentValue) * lerpFactor;

        // Snap when very close to avoid perpetual micro-drift
        if (Math.abs(this._currentValue - this._targetValue) < 0.0001) {
            this._currentValue = this._targetValue;
        }

        const progress = Math.max(0, Math.min(1, this._currentValue / duration));
        const padding = THUMB_ACTIVE_HALO;
        const trackWidth = w - padding * 2;
        const fillX = padding + progress * trackWidth;
        const centerY = h / 2;

        const isActive = this._dragging;
        const isHover = this._hovering;
        const trackH = (isHover || isActive) ? TRACK_HEIGHT_HOVER : TRACK_HEIGHT;

        // Determine target halo radius
        const targetHaloR = disabled ? 0 : (isActive ? THUMB_ACTIVE_HALO : (isHover ? THUMB_HOVER_HALO : 0));
        // Smoothly interpolate halo radius
        const haloLerp = 0.15;
        this._currentHaloR += (targetHaloR - this._currentHaloR) * haloLerp;
        if (Math.abs(this._currentHaloR - targetHaloR) < 0.3) {
            this._currentHaloR = targetHaloR;
        }

        const fillColor = disabled ? DISABLED_COLOR : FILL_COLOR;
        const thumbColor = disabled ? DISABLED_COLOR : THUMB_COLOR;

        // Clear
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);

        // Background track
        ctx.beginPath();
        ctx.roundRect(padding, centerY - trackH / 2, trackWidth, trackH, trackH / 2);
        ctx.fillStyle = TRACK_COLOR;
        ctx.fill();

        // Filled track
        if (progress > 0) {
            const filledWidth = Math.max(trackH, fillX - padding);
            ctx.beginPath();
            ctx.roundRect(padding, centerY - trackH / 2, filledWidth, trackH, trackH / 2);
            ctx.fillStyle = fillColor;
            ctx.fill();
        }

        // Halo behind thumb
        if (this._currentHaloR > 0.5) {
            ctx.beginPath();
            ctx.arc(fillX, centerY, this._currentHaloR, 0, Math.PI * 2);
            ctx.fillStyle = isActive ? 'rgba(255, 82, 82, 0.2)' : 'rgba(255, 255, 255, 0.08)';
            ctx.fill();
        }

        // Thumb
        if (!disabled) {
            ctx.beginPath();
            ctx.arc(fillX, centerY, THUMB_RADIUS, 0, Math.PI * 2);
            ctx.fillStyle = thumbColor;
            ctx.fill();
        }
    }

    _getTimeFromEvent = (e) => {
        const container = this._containerRef.current;
        if (!container) return 0;

        const rect = container.getBoundingClientRect();
        const padding = THUMB_ACTIVE_HALO;
        const trackWidth = rect.width - padding * 2;
        const x = e.clientX - rect.left - padding;
        const ratio = Math.max(0, Math.min(1, x / trackWidth));
        const { max } = this.props;
        return ratio * (max || 1);
    }

    _handleMouseDown = (e) => {
        const { disabled } = this.props;
        if (disabled) return;

        e.preventDefault();
        this._dragging = true;

        const time = this._getTimeFromEvent(e);
        this._targetValue = time;
        this._currentValue = time;

        if (this.props.onMouseDown) {
            this.props.onMouseDown();
        }
        if (this.props.onChange) {
            this.props.onChange(e, time);
        }

        window.addEventListener('mousemove', this._handleWindowMouseMove);
        window.addEventListener('mouseup', this._handleWindowMouseUp);
    }

    _handleWindowMouseMove = (e) => {
        if (!this._dragging) return;

        const time = this._getTimeFromEvent(e);
        this._targetValue = time;
        this._currentValue = time;

        if (this.props.onChange) {
            this.props.onChange(e, time);
        }
    }

    _handleWindowMouseUp = (e) => {
        if (!this._dragging) return;

        this._dragging = false;
        const time = this._getTimeFromEvent(e);
        this._targetValue = time;

        if (this.props.onChangeCommitted) {
            this.props.onChangeCommitted(e, time);
        }

        window.removeEventListener('mousemove', this._handleWindowMouseMove);
        window.removeEventListener('mouseup', this._handleWindowMouseUp);
    }

    _handleMouseEnter = () => {
        this._hovering = true;
    }

    _handleMouseLeave = () => {
        this._hovering = false;
    }

    render() {
        return (
            <div
                ref={this._containerRef}
                onMouseDown={this._handleMouseDown}
                onMouseEnter={this._handleMouseEnter}
                onMouseLeave={this._handleMouseLeave}
                style={{
                    width: '100%',
                    cursor: this.props.disabled ? 'default' : 'pointer',
                    userSelect: 'none',
                    touchAction: 'none',
                    position: 'relative'
                }}
                role='slider'
                aria-label={this.props['aria-label']}
                aria-valuemin={0}
                aria-valuemax={this.props.max || 0}
                aria-valuenow={this.props.value || 0}
                aria-valuetext={this.props['aria-valuetext']}
                tabIndex={this.props.disabled ? -1 : 0}
            >
                <canvas
                    ref={this._canvasRef}
                    style={{ display: 'block', width: '100%', height: CANVAS_HEIGHT + 'px' }}
                />
            </div>
        );
    }
}

export default ProgressSlider;
